// Stratul de densități pe graficul din Терминал плотностей.
//
// Fiecare zid viu e un segment orizontal care **începe în clipa în care a apărut** zidul și
// merge până la marginea din dreapta — lungimea lui pe grafic e chiar durata de viață. În
// dreapta are eticheta cu suma și vârsta, calculată la fiecare desen, deci minutele cresc
// singure pe grafic, fără să aștepte o nouă citire.
//
// Un zid **mâncat** nu dispare fără urmă: rămâne o linie portocaliu închis de la apariție
// până la momentul în care a fost mâncat, cu suma și cât a trăit. Așa se vede pe grafic
// unde a fost absorbită lichiditatea — locurile la care prețul revine adesea.

import { el } from '../core/util.js';

/** Ce arată graficul — bifele din colțul lui, ținute în browser. */
const SHOW_KEY = 'sx_wall_layer_show';
const SHOW_DEFAULT = { real: true, mm: false, young: false, traces: true };
const SHOW_ITEMS = [
  ['real', 'Реальные', 'стоят на месте ≥2 мин — кандидаты на отскок'],
  ['mm', 'Маркет-мейкер', 'переставляются вслед за ценой на том же расстоянии: двигают график, отскока от них не будет'],
  ['young', 'Молодые', 'появились меньше 2 мин назад — ещё непонятно, настоящие ли'],
  ['traces', 'Съеденные', 'оранжевые следы плотностей, которые съела цена'],
];

function citesteShow() {
  try {
    return { ...SHOW_DEFAULT, ...(JSON.parse(localStorage.getItem(SHOW_KEY) || '{}') || {}) };
  } catch {
    return { ...SHOW_DEFAULT };
  }
}

const C = {
  bid: '#26a69a',
  ask: '#ef5350',
  mm: '#8b93a7',             // market maker: gri, punctat — nu e un zid, e o momeală care se mută
  trace: '#c2410c',          // portocaliu închis — urma zidului mâncat
  text: '#ffffff',
};

const MIN_MINUTES = 1;
const MAX_LIVE = 8;
/** O etichetă împinsă mai departe de atât de linia ei nu se mai desenează — ar minți. */
const MAX_SHIFT = 26;

function bani(v) {
  const a = Math.abs(v || 0);
  if (a >= 1e6) return `$${(a / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `$${Math.round(a / 1e3)}K`;
  return `$${Math.round(a)}`;
}

/** Vârsta în cuvinte scurte: „<1 мин", „12 мин", „1 ч 05 мин". */
export function varsta(ms) {
  const m = Math.floor(Math.max(0, ms) / 60000);
  if (m < 1) return '<1 мин';
  if (m < 60) return `${m} мин`;
  return `${Math.floor(m / 60)} ч ${String(m % 60).padStart(2, '0')} мин`;
}

export class DensityLayer {
  constructor(chart) {
    this.chart = chart;
    this.live = [];
    this.traces = [];
    this.selected = null;          // { price, side }
    this.host = chart.mainEl;
    this.show = citesteShow();
    this.raw = { live: [], traces: [] };
    this.toggles = this.#toggles();
    this.host.append(this.toggles);
    this.canvas = el('canvas', { class: 'chart-whales-layer' });
    this.ctx = this.canvas.getContext('2d');
    this.host.append(this.canvas);
    this.ro = new ResizeObserver(() => this.#resize());
    this.ro.observe(this.host);
    this.#resize();
  }

  /**
   * Zidurile vii ale monedei și urmele celor mâncate. Pe grafic intră doar zidurile care
   * contează — cel puțin un minut de oborot (mediana), cele mai puternice 8, plus cel ales;
   * o monedă activă are zeci de niveluri mărunte care ar acoperi lumânările.
   */
  setData(live, traces, selected = this.selected) {
    this.raw = { live: live || [], traces: traces || [] };
    const ales = (z) => selected && z.price === selected.price && z.side === selected.side;
    const vreau = (z) => this.show[z.kind || 'real'] !== false;
    this.live = (live || []).filter((z) => ales(z) || (vreau(z) && (z.minutes || 0) >= MIN_MINUTES))
      .sort((a, b) => (b.minutes || 0) - (a.minutes || 0))
      .filter((z, i) => i < MAX_LIVE || ales(z));
    this.traces = this.show.traces ? (traces || []) : [];
    this.#syncToggles();
    this.selected = selected;
    // Graficul își redesenează straturile doar când se mișcă vederea; datele noi (și vârsta
    // care crește) trebuie desenate de noi — la fiecare citire, adică la 2.5 s.
    this.render();
  }

  /** Bifele din colțul graficului: ce tipuri de ziduri se văd. */
  #toggles() {
    const box = el('div', { class: 'dl-toggles' });
    this.checks = {};
    for (const [k, t, title] of SHOW_ITEMS) {
      const cb = el('input', {
        type: 'checkbox', checked: this.show[k],
        onchange: (e) => {
          this.show[k] = e.target.checked;
          try { localStorage.setItem(SHOW_KEY, JSON.stringify(this.show)); } catch { /* fără stocare */ }
          this.setData(this.raw.live, this.raw.traces);
        },
      });
      this.checks[k] = el('span', { class: 'dl-n' });
      box.append(el('label', { class: `dl-t ${k}`, title }, [cb, el('i', { class: `dl-sw ${k}` }), el('span', { text: t }), this.checks[k]]));
    }
    return box;
  }

  /** Câte ziduri de fiecare tip are moneda acum — ca să știi ce ascunde o bifă. */
  #syncToggles() {
    const n = { real: 0, mm: 0, young: 0 };
    for (const z of this.raw.live) if ((z.minutes || 0) >= MIN_MINUTES) n[z.kind || 'real'] = (n[z.kind || 'real'] || 0) + 1;
    n.traces = this.raw.traces.length;
    for (const [k, span] of Object.entries(this.checks)) span.textContent = n[k] ? String(n[k]) : '';
  }

  clear() {
    this.live = [];
    this.traces = [];
    this.selected = null;
    this.render();
  }

  #resize() {
    const dpr = window.devicePixelRatio || 1;
    const w = this.host.clientWidth;
    const h = this.host.clientHeight;
    this.canvas.width = w * dpr;
    this.canvas.height = h * dpr;
    this.canvas.style.width = `${w}px`;
    this.canvas.style.height = `${h}px`;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  /**
   * Coordonata x a unui moment oarecare (nu doar a deschiderii unei lumânări): între două
   * lumânări se interpolează, după ultima se merge înainte cu pasul barelor. Fără asta,
   * un zid apărut la 00:35:17 n-avea coordonată și se desena de la marginea stângă.
   */
  #x(ms) {
    const bars = this.chart.bars;
    const ts = this.chart.chart.timeScale();
    if (!bars?.length) return null;
    const t = ms / 1000;
    const n = bars.length;
    const c = (i) => ts.timeToCoordinate(bars[i].time);
    if (t <= bars[0].time) return c(0);
    if (t >= bars[n - 1].time) {
      const last = c(n - 1);
      if (last === null) return null;
      const pas = n > 1 ? bars[n - 1].time - bars[n - 2].time : 60;
      const lat = ts.options().barSpacing || 6;
      return last + ((t - bars[n - 1].time) / pas) * lat;
    }
    let lo = 0;
    let hi = n - 1;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (bars[mid].time <= t) lo = mid; else hi = mid;
    }
    const a = c(lo);
    const b = c(hi);
    if (a === null || b === null) return a ?? b;
    return a + ((t - bars[lo].time) / (bars[hi].time - bars[lo].time)) * (b - a);
  }

  render() {
    const ctx = this.ctx;
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.clearRect(0, 0, this.canvas.width / dpr, this.canvas.height / dpr);
    const series = this.chart.candles;
    if (!series || !this.chart.bars?.length || (!this.live.length && !this.traces.length)) return;
    let axa = 60;
    try { axa = this.chart.chart.priceScale('right').width() || 60; } catch { /* implicit */ }
    const w = this.host.clientWidth - axa;
    const h = this.host.clientHeight;
    const acum = Date.now();
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, w, h);
    ctx.clip();
    ctx.font = '600 10px Manrope, sans-serif';
    ctx.textBaseline = 'middle';
    const etichete = [];

    // ------------------------------------------------ urmele zidurilor mâncate
    for (const t of this.traces) {
      const y = series.priceToCoordinate(t.price);
      if (y === null || y < -5 || y > h + 5) continue;
      const x1 = this.#x(t.ended);
      if (x1 === null || x1 < 0) continue;
      const x0 = Math.max(0, this.#x(t.first) ?? 0);
      ctx.strokeStyle = C.trace;
      ctx.lineWidth = 2.5;
      ctx.beginPath(); ctx.moveTo(x0, y); ctx.lineTo(Math.min(x1, w), y); ctx.stroke();
      // capătul: clipa în care a fost mâncat
      ctx.fillStyle = C.trace;
      ctx.beginPath(); ctx.arc(Math.min(x1, w), y, 3, 0, Math.PI * 2); ctx.fill();
      // Eticheta urmei stă la stânga începutului ei (zona liberă a graficului): nici linia,
      // nici etichetele zidurilor vii din dreapta nu o acoperă.
      const text = `съедена ${bani(t.peak)} · жила ${varsta(t.ended - t.first)}`;
      const lat = ctx.measureText(text).width + 10;
      if (x0 - 6 - lat >= 0) this.#label(ctx, text, x0 - 6, y, C.trace, w, 'right');
      else this.#label(ctx, text, x0 + 2, y - 12, C.trace, w, 'left');
    }

    // ------------------------------------------------ zidurile vii
    for (const z of this.live) {
      const y = series.priceToCoordinate(z.price);
      if (y === null || y < -5 || y > h + 5) continue;
      const mm = z.kind === 'mm';
      const col = mm ? C.mm : z.side === 'bid' ? C.bid : C.ask;
      const ales = this.selected && this.selected.price === z.price && this.selected.side === z.side;
      const x0 = Math.max(0, this.#x(z.first) ?? 0);
      const gros = Math.min(5, 1.5 + (z.minutes || 0) / 3);
      if (ales) {
        ctx.strokeStyle = `${col}55`;
        ctx.lineWidth = gros + 6;
        ctx.beginPath(); ctx.moveTo(x0, y); ctx.lineTo(w, y); ctx.stroke();
      }
      ctx.strokeStyle = col;
      ctx.lineWidth = mm ? 1.5 : gros;
      if (mm || z.kind === 'young') ctx.setLineDash(mm ? [3, 4] : [6, 3]);
      ctx.beginPath(); ctx.moveTo(x0, y); ctx.lineTo(w, y); ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = col;
      ctx.beginPath(); ctx.arc(x0, y, 3, 0, Math.PI * 2); ctx.fill();
      const mancat = z.peak > z.usd * 1.1 ? ` · съедено ${Math.round((1 - z.usd / z.peak) * 100)}%` : '';
      const min = (z.minutes || 0) >= 1 ? Math.round(z.minutes) : (z.minutes || 0).toFixed(1);
      const text = mm
        ? `ММ ${bani(z.usd)} · переставлялась ${z.moves}× за ${varsta((z.chain_age || 0) * 1000)}`
        : `${bani(z.usd)} · живёт ${varsta(acum - z.first)} · ${min} мин об.${mancat}`;
      etichete.push({ text, x: w - 6, y, col, align: 'right', ales });
    }
    // Două ziduri la prețuri apropiate: etichetele se așază una sub alta, nu una peste alta.
    etichete.sort((a, b) => a.y - b.y);
    let jos = -Infinity;
    for (const e of etichete) {
      const y = Math.max(e.y, jos + 17);
      if (y - e.y > MAX_SHIFT && !e.ales) continue;
      jos = y;
      this.#label(ctx, e.text, e.x, y, e.col, w, e.align);
    }
    ctx.restore();
  }

  #label(ctx, text, x, y, col, w, align) {
    const tw = ctx.measureText(text).width + 10;
    let x0 = align === 'right' ? x - tw : x;
    if (x0 + tw > w) x0 = w - tw;
    if (x0 < 0) x0 = 0;
    ctx.fillStyle = col;
    ctx.beginPath();
    ctx.roundRect ? ctx.roundRect(x0, y - 8, tw, 16, 4) : ctx.rect(x0, y - 8, tw, 16);
    ctx.fill();
    ctx.fillStyle = C.text;
    ctx.fillText(text, x0 + 5, y + 0.5);
  }

  destroy() {
    this.ro?.disconnect();
    this.canvas?.remove();
    this.toggles?.remove();
  }
}
